import { hasPermission, PERMISSIONS, type PermissionDefinition } from "@edm/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, KeyRound, Plus, RefreshCw, ShieldCheck, UserCog } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { EmptyState } from "@/components/empty-state";
import { PageHeader } from "@/components/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ApiError, apiFetch } from "@/lib/api";
import { useMe } from "@/lib/auth";
import {
  createResource,
  listResource,
  putResource,
  type UserDataScopeRow,
  updateResource,
  useReferenceData,
} from "@/lib/master-data";
import { formatDateTime } from "@/lib/utils";

interface UserRow {
  id: string;
  displayName: string;
  status: string;
  mustChangePassword: boolean;
  lastLoginAt: string | null;
  deletedAt: string | null;
  identifiers: { type: string; value: string }[];
  roles: { id: string; code: string; name: string }[];
}

interface ApprovalDutyRow {
  id: string;
  userId: string;
  userName: string;
  dutyType: "cost_center_owner";
  scopeCompanyId: string | null;
  scopeDepartmentId: string | null;
  activeFrom: string;
  activeTo: string | null;
}

interface RoleRow {
  id: string;
  code: string;
  name: string;
  description: string | null;
  isSystem: boolean;
  isActive: boolean;
  permissionCodes: string[];
  userCount: number;
}

const IDENTIFIER_LABELS: Record<string, string> = {
  phone: "手机号",
  email: "邮箱",
  adid: "ADID",
};

function permissionGroups(): { group: string; items: PermissionDefinition[] }[] {
  const groups = new Map<string, PermissionDefinition[]>();
  for (const permission of PERMISSIONS) {
    const list = groups.get(permission.group) ?? [];
    list.push(permission);
    groups.set(permission.group, list);
  }
  return [...groups.entries()].map(([group, items]) => ({ group, items }));
}

export function SettingsAccessPage() {
  const { data: me } = useMe();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<"users" | "roles" | "duties">("users");
  const [dutyOpen, setDutyOpen] = useState(false);
  const [dutyForm, setDutyForm] = useState({
    userId: "",
    dutyType: "cost_center_owner" as const,
    scopeDepartmentId: "",
    activeTo: "",
  });
  const [createOpen, setCreateOpen] = useState(false);
  const [roleEditUser, setRoleEditUser] = useState<UserRow | null>(null);
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<RoleRow | null>(null);
  const [permissionCodes, setPermissionCodes] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [scopeUser, setScopeUser] = useState<UserRow | null>(null);
  const [scopeType, setScopeType] = useState("self");
  const [scopeId, setScopeId] = useState("");
  const [createForm, setCreateForm] = useState({
    displayName: "",
    identifierType: "email",
    identifierValue: "",
    initialPassword: "",
    roleIds: [] as string[],
  });

  const usersQuery = useQuery({
    queryKey: ["admin-users"],
    queryFn: () => listResource<UserRow>("/api/v1/admin/users"),
  });
  const rolesQuery = useQuery({
    queryKey: ["admin-roles"],
    queryFn: () => listResource<RoleRow>("/api/v1/admin/roles"),
  });
  const dutiesQuery = useQuery({
    queryKey: ["approval-duties"],
    queryFn: () => apiFetch<ApprovalDutyRow[]>("/api/v1/admin/approval-duties"),
  });
  const roles = rolesQuery.data ?? [];
  const users = usersQuery.data ?? [];
  const reference = useReferenceData();
  const scopesQuery = useQuery({
    queryKey: ["user-data-scopes", scopeUser?.id],
    queryFn: () => apiFetch<UserDataScopeRow[]>(`/api/v1/admin/users/${scopeUser?.id}/data-scopes`),
    enabled: Boolean(scopeUser),
  });
  const companies = reference.data?.companies ?? [];
  const departments = reference.data?.departments ?? [];
  const warehouses = reference.data?.warehouses ?? [];
  const scopeOptions =
    scopeType === "company"
      ? companies.map((row) => ({ id: row.id, name: row.name }))
      : scopeType === "department" || scopeType === "cost_center"
        ? departments.map((row) => ({ id: row.id, name: row.name }))
        : scopeType === "warehouse"
          ? warehouses.map((row) => ({ id: row.id, name: row.name }))
          : [];
  const needsScopeId = ["company", "department", "cost_center", "warehouse"].includes(scopeType);
  const canManageUsers = Boolean(me && hasPermission(me.permissions, "admin:users:manage"));
  const canManageRoles = Boolean(me && hasPermission(me.permissions, "admin:access:manage"));
  const groups = useMemo(permissionGroups, []);

  async function createUser() {
    if (!createForm.displayName.trim() || !createForm.identifierValue.trim()) {
      toast.error("请填写姓名和登录标识");
      return;
    }
    if (createForm.roleIds.length === 0) {
      toast.error("请至少选择一个角色");
      return;
    }
    setSaving(true);
    try {
      const result = await createResource<{ id: string; temporaryPassword: string | null }>(
        "/api/v1/admin/users",
        {
          displayName: createForm.displayName.trim(),
          identifierType: createForm.identifierType,
          identifierValue: createForm.identifierValue.trim(),
          roleIds: createForm.roleIds,
          ...(createForm.initialPassword ? { initialPassword: createForm.initialPassword } : {}),
        },
      );
      setCreateOpen(false);
      setCreateForm({
        displayName: "",
        identifierType: "email",
        identifierValue: "",
        initialPassword: "",
        roleIds: [],
      });
      if (result.temporaryPassword) setTempPassword(result.temporaryPassword);
      toast.success("用户已创建，首次登录需修改密码");
      await queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "用户创建失败");
    } finally {
      setSaving(false);
    }
  }

  async function toggleStatus(user: UserRow) {
    try {
      await updateResource(`/api/v1/admin/users/${user.id}/status`, {
        isActive: user.status !== "active",
      });
      toast.success(user.status === "active" ? "账号已停用" : "账号已启用");
      await queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "操作失败");
    }
  }

  async function resetPassword(user: UserRow) {
    try {
      const result = await createResource<{ temporaryPassword: string }>(
        `/api/v1/admin/users/${user.id}/reset-password`,
        {},
      );
      setTempPassword(result.temporaryPassword);
      await queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "重置密码失败");
    }
  }

  function openRoleEdit(user: UserRow) {
    setRoleEditUser(user);
    setSelectedRoleIds(user.roles.map((role) => role.id));
  }

  async function saveUserRoles() {
    if (!roleEditUser) return;
    if (selectedRoleIds.length === 0) {
      toast.error("请至少保留一个角色");
      return;
    }
    try {
      await putResource(`/api/v1/admin/users/${roleEditUser.id}/roles`, {
        roleIds: selectedRoleIds,
      });
      toast.success("角色已更新");
      setRoleEditUser(null);
      await queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "角色更新失败");
    }
  }

  function openRolePermissions(role: RoleRow) {
    setSelectedRole(role);
    setPermissionCodes(role.permissionCodes);
  }

  async function addDataScope() {
    if (!scopeUser) return;
    if (needsScopeId && !scopeId) return toast.error("请选择数据范围对象");
    try {
      await createResource(`/api/v1/admin/users/${scopeUser.id}/data-scopes`, {
        scopeType,
        scopeId: needsScopeId ? scopeId : null,
      });
      toast.success("数据范围已添加");
      setScopeId("");
      await scopesQuery.refetch();
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "添加失败");
    }
  }

  async function removeDataScope(scopeIdValue: string) {
    if (!scopeUser) return;
    try {
      await apiFetch(`/api/v1/admin/users/${scopeUser.id}/data-scopes/${scopeIdValue}`, {
        method: "DELETE",
      });
      toast.success("数据范围已移除");
      await scopesQuery.refetch();
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "移除失败");
    }
  }

  async function saveRolePermissions() {
    if (!selectedRole) return;
    setSaving(true);
    try {
      await putResource(`/api/v1/admin/roles/${selectedRole.id}/permissions`, {
        permissionCodes,
      });
      toast.success("角色权限已更新");
      setSelectedRole(null);
      await queryClient.invalidateQueries({ queryKey: ["admin-roles"] });
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "权限保存失败");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="用户与角色"
        description="账号、角色权限矩阵与数据范围。系统管理员默认只拥有配置与审计能力，不自动获得设备业务办理权。"
        actions={
          <>
            <Button
              variant="outline"
              onClick={() => {
                usersQuery.refetch();
                rolesQuery.refetch();
              }}
            >
              <RefreshCw className="h-4 w-4" />
              刷新
            </Button>
            {tab === "users" && canManageUsers ? (
              <Button onClick={() => setCreateOpen(true)}>
                <Plus className="h-4 w-4" />
                新建用户
              </Button>
            ) : null}
            {tab === "duties" && canManageUsers ? (
              <Button onClick={() => setDutyOpen(true)}>
                <Plus className="h-4 w-4" />
                新增审批职责
              </Button>
            ) : null}
          </>
        }
      />

      <Tabs value={tab} onValueChange={(value) => setTab(value as "users" | "roles" | "duties")}>
        <TabsList>
          <TabsTrigger value="users">用户</TabsTrigger>
          <TabsTrigger value="roles">角色权限</TabsTrigger>
          <TabsTrigger value="duties">审批职责</TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === "users" ? (
        <Card>
          <CardHeader>
            <CardTitle>用户列表</CardTitle>
            <CardDescription>
              一个用户可拥有多个角色，功能权限与数据范围按并集生效。
            </CardDescription>
          </CardHeader>
          <CardContent>
            {usersQuery.isLoading ? (
              <div className="space-y-2">
                {["u1", "u2", "u3", "u4"].map((key) => (
                  <Skeleton key={key} className="h-11 w-full" />
                ))}
              </div>
            ) : users.length === 0 ? (
              <EmptyState title="还没有用户" description="新建用户后，系统会生成一次性临时密码。" />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>姓名</TableHead>
                    <TableHead>登录标识</TableHead>
                    <TableHead>角色</TableHead>
                    <TableHead>状态</TableHead>
                    <TableHead>最后登录</TableHead>
                    <TableHead className="text-right">操作</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">{user.displayName}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {user.identifiers
                          .map(
                            (identifier) =>
                              `${IDENTIFIER_LABELS[identifier.type] ?? identifier.type}：${identifier.value}`,
                          )
                          .join(" / ")}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {user.roles.map((role) => (
                            <Badge key={role.id} variant="secondary">
                              {role.name}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={user.status === "active" ? "success" : "secondary"}>
                          {user.status === "active" ? "启用" : "停用"}
                        </Badge>
                        {user.mustChangePassword ? (
                          <Badge variant="warning" className="ml-1">
                            待改密
                          </Badge>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {formatDateTime(user.lastLoginAt)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openRoleEdit(user)}>
                            <UserCog className="h-3.5 w-3.5" />
                            角色
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setScopeUser(user)}>
                            范围
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => resetPassword(user)}>
                            <KeyRound className="h-3.5 w-3.5" />
                            重置
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => toggleStatus(user)}>
                            {user.status === "active" ? "停用" : "启用"}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      ) : tab === "roles" ? (
        <div className="grid gap-6 xl:grid-cols-[0.8fr_1.4fr]">
          <Card>
            <CardHeader>
              <CardTitle>角色</CardTitle>
              <CardDescription>系统内置角色可调整权限，但不能删除。</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {roles.map((role) => (
                <button
                  key={role.id}
                  type="button"
                  onClick={() => openRolePermissions(role)}
                  className="flex w-full items-start justify-between gap-3 rounded-lg border border-border p-3 text-left transition-colors hover:bg-muted/60"
                >
                  <span>
                    <span className="flex items-center gap-2 text-sm font-medium">
                      {role.name}
                      {role.isSystem ? <Badge variant="outline">内置</Badge> : null}
                    </span>
                    <span className="mt-1 block text-xs text-muted-foreground">
                      {role.description ?? role.code} · {role.userCount} 人
                    </span>
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {role.permissionCodes.length} 项
                  </span>
                </button>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{selectedRole ? `权限：${selectedRole.name}` : "权限矩阵"}</CardTitle>
              <CardDescription>
                {selectedRole
                  ? "勾选后保存；系统管理员角色必须保留“角色与授权管理”。"
                  : "从左侧选择角色查看和调整权限。"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!selectedRole ? (
                <EmptyState title="请选择角色" description="左侧角色列表中选择一个角色。" />
              ) : (
                <div className="space-y-5">
                  {groups.map((group) => (
                    <div key={group.group}>
                      <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        {group.group}
                      </p>
                      <div className="grid gap-2 sm:grid-cols-2">
                        {group.items.map((permission) => {
                          const checked = permissionCodes.includes(permission.code);
                          const locked =
                            selectedRole.code === "system_admin" &&
                            permission.code === "admin:access:manage";
                          return (
                            <label
                              key={permission.code}
                              className="flex items-start gap-2 rounded-md border border-border px-3 py-2 text-xs"
                            >
                              <input
                                type="checkbox"
                                className="mt-0.5 h-3.5 w-3.5"
                                checked={checked}
                                disabled={locked || !canManageRoles}
                                onChange={(event) => {
                                  setPermissionCodes((current) =>
                                    event.target.checked
                                      ? [...current, permission.code]
                                      : current.filter((code) => code !== permission.code),
                                  );
                                }}
                              />
                              <span>
                                <span className="block font-medium">{permission.name}</span>
                                <span className="block font-mono text-[10px] text-muted-foreground">
                                  {permission.code}
                                </span>
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setSelectedRole(null)}>
                      取消
                    </Button>
                    <Button onClick={saveRolePermissions} disabled={saving || !canManageRoles}>
                      <ShieldCheck className="h-4 w-4" />
                      保存权限
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>审批职责</CardTitle>
            <CardDescription>
              成本中心负责人职责（部门负责人）；直属上级由人员信息中的上下级关系决定，不在此处配置。
            </CardDescription>
          </CardHeader>
          <CardContent>
            {dutiesQuery.isLoading ? (
              <Skeleton className="h-40 w-full" />
            ) : (dutiesQuery.data ?? []).length === 0 ? (
              <EmptyState
                title="还没有审批职责"
                description="新增后，审批矩阵会按职责与部门匹配审批人。"
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>用户</TableHead>
                    <TableHead>职责</TableHead>
                    <TableHead>部门</TableHead>
                    <TableHead>生效时间</TableHead>
                    <TableHead>失效时间</TableHead>
                    <TableHead>状态</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(dutiesQuery.data ?? []).map((duty) => {
                    const expired = Boolean(
                      duty.activeTo && duty.activeTo < new Date().toISOString(),
                    );
                    return (
                      <TableRow key={duty.id}>
                        <TableCell className="font-medium">{duty.userName}</TableCell>
                        <TableCell>成本中心负责人</TableCell>
                        <TableCell>
                          {departments.find((item) => item.id === duty.scopeDepartmentId)?.name ??
                            duty.scopeDepartmentId ??
                            "—"}
                        </TableCell>
                        <TableCell>{formatDateTime(duty.activeFrom)}</TableCell>
                        <TableCell>
                          {duty.activeTo ? formatDateTime(duty.activeTo) : "长期"}
                        </TableCell>
                        <TableCell>
                          <Badge variant={expired ? "secondary" : "success"}>
                            {expired ? "已失效" : "生效中"}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={dutyOpen} onOpenChange={setDutyOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>新增审批职责</DialogTitle>
            <DialogDescription>选择用户、职责类型与部门范围。</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>用户</Label>
              <select
                className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm"
                value={dutyForm.userId}
                onChange={(event) => setDutyForm({ ...dutyForm, userId: event.target.value })}
              >
                <option value="">选择用户</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.displayName}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label>职责类型</Label>
              <select
                className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm"
                value={dutyForm.dutyType}
                onChange={(event) =>
                  setDutyForm({
                    ...dutyForm,
                    dutyType: "cost_center_owner",
                  })
                }
              >
                <option value="cost_center_owner">成本中心负责人</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>部门范围（可选）</Label>
              <select
                className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm"
                value={dutyForm.scopeDepartmentId}
                onChange={(event) =>
                  setDutyForm({ ...dutyForm, scopeDepartmentId: event.target.value })
                }
              >
                <option value="">全部部门</option>
                {departments.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label>失效时间（可选）</Label>
              <Input
                type="date"
                value={dutyForm.activeTo}
                onChange={(event) => setDutyForm({ ...dutyForm, activeTo: event.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDutyOpen(false)}>
              取消
            </Button>
            <Button
              disabled={!dutyForm.userId}
              onClick={async () => {
                try {
                  await createResource("/api/v1/admin/approval-duties", {
                    userId: dutyForm.userId,
                    dutyType: dutyForm.dutyType,
                    scopeDepartmentId: dutyForm.scopeDepartmentId || null,
                    activeTo: dutyForm.activeTo
                      ? new Date(`${dutyForm.activeTo}T23:59:59+08:00`).toISOString()
                      : null,
                  });
                  await queryClient.invalidateQueries({ queryKey: ["approval-duties"] });
                  setDutyOpen(false);
                  setDutyForm({
                    userId: "",
                    dutyType: "cost_center_owner",
                    scopeDepartmentId: "",
                    activeTo: "",
                  });
                  toast.success("审批职责已新增");
                } catch (error) {
                  toast.error(error instanceof Error ? error.message : "新增失败");
                }
              }}
            >
              保存
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>新建用户</DialogTitle>
            <DialogDescription>
              未填写初始密码时，系统生成一次性临时密码并强制首次改密。
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>姓名</Label>
              <Input
                value={createForm.displayName}
                onChange={(event) =>
                  setCreateForm({ ...createForm, displayName: event.target.value })
                }
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-[140px_1fr]">
              <div className="space-y-2">
                <Label>标识类型</Label>
                <select
                  value={createForm.identifierType}
                  onChange={(event) =>
                    setCreateForm({ ...createForm, identifierType: event.target.value })
                  }
                  className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm"
                >
                  <option value="email">邮箱</option>
                  <option value="phone">手机号</option>
                  <option value="adid">ADID</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label>登录标识</Label>
                <Input
                  value={createForm.identifierValue}
                  onChange={(event) =>
                    setCreateForm({ ...createForm, identifierValue: event.target.value })
                  }
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>初始密码（选填，至少 12 位）</Label>
              <Input
                type="password"
                value={createForm.initialPassword}
                onChange={(event) =>
                  setCreateForm({ ...createForm, initialPassword: event.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label>角色</Label>
              <div className="grid gap-2 sm:grid-cols-2">
                {roles.map((role) => (
                  <label
                    key={role.id}
                    className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={createForm.roleIds.includes(role.id)}
                      onChange={(event) =>
                        setCreateForm({
                          ...createForm,
                          roleIds: event.target.checked
                            ? [...createForm.roleIds, role.id]
                            : createForm.roleIds.filter((id) => id !== role.id),
                        })
                      }
                      className="h-4 w-4"
                    />
                    {role.name}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              取消
            </Button>
            <Button onClick={createUser} disabled={saving}>
              {saving ? "创建中…" : "创建用户"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(roleEditUser)} onOpenChange={(open) => !open && setRoleEditUser(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>调整角色：{roleEditUser?.displayName}</DialogTitle>
            <DialogDescription>保存后立即生效；不能移除最后一个系统管理员。</DialogDescription>
          </DialogHeader>
          <div className="grid gap-2 sm:grid-cols-2">
            {roles.map((role) => (
              <label
                key={role.id}
                className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm"
              >
                <input
                  type="checkbox"
                  checked={selectedRoleIds.includes(role.id)}
                  onChange={(event) =>
                    setSelectedRoleIds(
                      event.target.checked
                        ? [...selectedRoleIds, role.id]
                        : selectedRoleIds.filter((id) => id !== role.id),
                    )
                  }
                  className="h-4 w-4"
                />
                {role.name}
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleEditUser(null)}>
              取消
            </Button>
            <Button onClick={saveUserRoles}>保存角色</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(scopeUser)} onOpenChange={(open) => !open && setScopeUser(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>数据范围：{scopeUser?.displayName}</DialogTitle>
            <DialogDescription>
              数据范围与功能权限叠加生效；未配置时资产台账默认只看本人。
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {(scopesQuery.data ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">尚未配置数据范围（默认本人）。</p>
            ) : (
              (scopesQuery.data ?? []).map((scope) => (
                <div
                  key={scope.id}
                  className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm"
                >
                  <span>
                    {scope.scopeType}
                    {scope.scopeId ? ` · ${scope.scopeId.slice(0, 8)}…` : ""}
                  </span>
                  <Button variant="ghost" size="sm" onClick={() => removeDataScope(scope.id)}>
                    移除
                  </Button>
                </div>
              ))
            )}
            <div className="grid gap-2 sm:grid-cols-2">
              <select
                value={scopeType}
                onChange={(event) => {
                  setScopeType(event.target.value);
                  setScopeId("");
                }}
                className="h-10 rounded-md border border-input bg-card px-3 text-sm"
              >
                <option value="self">本人</option>
                <option value="own_department">本部门</option>
                <option value="own_cost_center">本成本中心</option>
                <option value="own_company">本公司</option>
                <option value="company">指定公司</option>
                <option value="department">指定部门</option>
                <option value="cost_center">指定成本中心</option>
                <option value="warehouse">指定仓库</option>
                <option value="all">全部</option>
              </select>
              {needsScopeId ? (
                <select
                  value={scopeId}
                  onChange={(event) => setScopeId(event.target.value)}
                  className="h-10 rounded-md border border-input bg-card px-3 text-sm"
                >
                  <option value="">选择对象</option>
                  {scopeOptions.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              ) : (
                <span />
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScopeUser(null)}>
              关闭
            </Button>
            <Button onClick={addDataScope}>添加范围</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(tempPassword)} onOpenChange={(open) => !open && setTempPassword(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>一次性临时密码</DialogTitle>
            <DialogDescription>
              请通过安全渠道交给用户；关闭后无法再次查看。用户首次登录必须修改密码。
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <Input readOnly value={tempPassword ?? ""} className="font-mono" />
            <Button
              variant="outline"
              onClick={async () => {
                if (tempPassword) {
                  await navigator.clipboard.writeText(tempPassword);
                  toast.success("已复制");
                }
              }}
            >
              <Copy className="h-4 w-4" />
              复制
            </Button>
          </div>
          <DialogFooter>
            <Button onClick={() => setTempPassword(null)}>我已记录</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
